import { Empty } from 'antd';
import { useSession } from 'next-auth/react';
import router from 'next/router';
import { CSSProperties, HTMLAttributes, useMemo, useRef } from 'react';
import {
    IAudioMessageProps,
    IFileMessageProps,
    IPhotoMessageProps,
    IVideoMessageProps,
    MessageList,
    MessageType
} from 'react-chat-elements';
import { useScroll } from '../../../hooks/useScroll';
import { Attachment, Comment } from '../../../model/types';
import { useGSocialStatus } from '../../../store/provider/GSocialStatusProvider';
import { PRIMARY_COLOR, TICKET_STATUS_COLOR_SOLVED } from '../../../styles/theme';
import { isInternalLink } from '../../../utils/routeHelper';
import { URLPreviewData, getUrlPreviews } from '../../../utils/urlPreview';
import SanitizedHtmlComponent from '../../SanitizedHtmlComponent';
import {
    acceptedAudioTypeMap,
    acceptedFileTypeMap,
    acceptedImageTypeMap,
    acceptedVideoTypeMap
} from '../../plugins/chat/types';
import ChatImage from './ChatImage';
import ChatInput from './ChatInput';
import ChatInstagramCaption from './ChatInstagramCaption';
import URLPreview from './URLPreview';

interface GSocialChatListProps extends HTMLAttributes<HTMLDivElement> {
    comments: Comment[];
}

interface BoxOptions {
    attachment?: Attachment;
    isLastAttachment?: boolean;
    isMine: boolean;
    previewUrl?: {
        url: string;
        promise: Promise<URLPreviewData[]>;
    };
}

interface UrlAndIndex {
    url: string;
    key: string;
}

interface CommentIdAndUrl {
    commentId: number;
    url: string;
}

const styles: Record<string, CSSProperties> = {
    root: { padding: '0 1rem' },
    hidden: { display: 'none' },
    sticky: { backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 1, paddingTop: '0.5rem' },
    chatHistoryLoading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' },

    chatContainer: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: '1rem',
        overflow: 'hidden',
        flex: 1
    },
    chatListContainer: {
        flex: 1,
        overflowY: 'auto'
    },
    chatInputContainer: {
        flex: '0 0 auto',
        position: 'relative'
    }
};

const ACCEPTED_AUDIO_TYPE_LIST = Object.keys(acceptedAudioTypeMap);
const ACCEPTED_FILE_TYPE_LIST = Object.keys(acceptedFileTypeMap);
const ACCEPTED_IMAGE_TYPE_LIST = Object.keys(acceptedImageTypeMap);
const ACCEPTED_VIDEO_TYPE_LIST = Object.keys(acceptedVideoTypeMap);
export const ACCEPTED_ALL_TYPE_LIST = [
    ...ACCEPTED_AUDIO_TYPE_LIST,
    ACCEPTED_FILE_TYPE_LIST,
    ACCEPTED_IMAGE_TYPE_LIST,
    ACCEPTED_VIDEO_TYPE_LIST
];

function getMessageTypeByAttachment(
    attachment: Attachment
): Record<'originalMessageType' | 'computedMessageType', MessageType['type']> {
    let originalMessageType: MessageType['type'] = 'text';

    if (ACCEPTED_AUDIO_TYPE_LIST.includes(attachment.mimeType)) originalMessageType = 'audio';
    if (ACCEPTED_FILE_TYPE_LIST.includes(attachment.mimeType)) originalMessageType = 'file';
    if (ACCEPTED_IMAGE_TYPE_LIST.includes(attachment.mimeType)) originalMessageType = 'photo';
    if (ACCEPTED_VIDEO_TYPE_LIST.includes(attachment.mimeType)) originalMessageType = 'video';

    // If the attachment is a photo, we want to display it as a text message.
    const computedMessageType = originalMessageType === 'photo' ? 'text' : originalMessageType;

    return { originalMessageType, computedMessageType };
}

function getHumanReadableFileSize(fileSizeInBytes: number): string {
    const limit = 1024;
    const fileSizeInKBs = Math.round(Number(fileSizeInBytes) / limit);
    if (isNaN(fileSizeInKBs)) return '';

    const showInKBs = fileSizeInKBs < limit;
    return showInKBs ? `${fileSizeInKBs} KB` : `${Math.round(fileSizeInKBs / limit)} MB`;
}

function getFirstUrlsInComments(comments: Comment[]): CommentIdAndUrl[] {
    return comments
        .map((comment) => {
            return {
                commentId: comment.id,
                url: getFirstUrlInText(comment.body)
            };
        })
        .filter((comment) => comment.url);
}

function getCommentsAndAttachmentsAsBox(comments: Comment[], userId: number): MessageType[] {
    const commentsAndAttachments: MessageType[] = [];

    const firstUrlsInComments = getFirstUrlsInComments(comments);
    let urlPreviewsPromise;

    try {
        urlPreviewsPromise =
            firstUrlsInComments.length > 0 ? getUrlPreviews(firstUrlsInComments.map(({ url }) => url)) : undefined;
    } catch (err) {
        //
    }

    comments.forEach((comment) => {
        const isMine = comment.creator.id === userId;
        const firstUrl = firstUrlsInComments.find(({ commentId }) => commentId === comment.id)?.url;
        const previewUrl = firstUrl && urlPreviewsPromise ? { url: firstUrl, promise: urlPreviewsPromise } : undefined;

        if (comment.attachments.length > 0) {
            comment.attachments.forEach((attachment, index) => {
                let isLastAttachment = false;

                if (index === comment.attachments.length - 1) {
                    isLastAttachment = true;
                }

                commentsAndAttachments.push(
                    convertToBox(comment, {
                        attachment,
                        isLastAttachment,
                        isMine,
                        previewUrl
                    })
                );

                if (getMessageTypeByAttachment(attachment).originalMessageType === 'file') {
                    commentsAndAttachments.push(convertToBox(comment, { isMine, previewUrl }));
                }
            });
        } else {
            commentsAndAttachments.push(convertToBox(comment, { isMine, previewUrl }));
        }
    });

    return commentsAndAttachments;
}

function injectUrlsToComments(comments: Comment[]): Comment[] {
    return comments.map((comment) => {
        const extractedUrls = extractUrls(comment.body);
        const urlsAndIndexes = getUrlIndexes(comment.body, extractedUrls);

        comment.body = transformUrlsToAnchorTags(comment.body, urlsAndIndexes);

        return comment;
    });
}

function getFirstUrlInText(text: string): string | null {
    const urlInHref = text.match(/href="([^"]*)"/);
    if (urlInHref) return urlInHref[1];
}

function extractUrls(text: string): string[] {
    try {
        const dom = new DOMParser().parseFromString(text, 'text/html');

        dom.querySelectorAll('a').forEach((url) => url.remove());

        dom.body.querySelectorAll('*').forEach((node) => {
            node.insertAdjacentText('beforebegin', '\n');
        });

        const urlRegExp = /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-/]))?/gm;

        return dom.body.textContent.match(urlRegExp) || [];
    } catch (err) {
        return [];
    }
}

function getUrlIndexes(text: string, urls: string[]): UrlAndIndex[] {
    const urlsAndIndexes = [];

    urls.forEach((url) => {
        const urlStartIndex = text.indexOf(url);
        const urlEndIndex = urlStartIndex + url.length;
        const key = `{{${urlStartIndex},${urlEndIndex}}}`;

        urlsAndIndexes.push({ url, key });
    });

    return urlsAndIndexes;
}

function transformUrlsToAnchorTags(text: string, urlsAndIndexes: UrlAndIndex[]): string {
    urlsAndIndexes.forEach(({ key, url }) => {
        text = text.replace(url, key);
    });

    urlsAndIndexes.forEach(({ url, key }) => {
        text = text.replace(key, `<a href="${url}" target="_blank">${url}</a>`);
    });

    return text;
}

function getBoxText(comment: Comment, options: BoxOptions): JSX.Element {
    let commentBody = comment.body;

    if (options?.attachment) {
        const { originalMessageType } = getMessageTypeByAttachment(options.attachment);

        if (originalMessageType === 'file') {
            return <span>{options.attachment.filename}</span>;
        }

        if (
            ['photo', 'video'].includes(originalMessageType) &&
            comment.channel === 'INSTAGRAM_COMMENT' &&
            options.previewUrl
        ) {
            // remove the first anchor tag
            commentBody = commentBody.replace(/<a[^>]*>([^<]+)<\/a>/, '');
            // ltrim <br> tags
            commentBody = commentBody.replace(/^(<br>)+/, '');

            return (
                <>
                    {originalMessageType === 'photo' && (
                        <ChatImage uri={options.attachment.objectUrl} href={options.previewUrl.url} />
                    )}
                    <ChatInstagramCaption url={options.previewUrl.url} urlPreviewPromise={options.previewUrl.promise} />
                    <div style={{ padding: '0.5rem' }}>
                        <SanitizedHtmlComponent dirtyHtml={commentBody} />
                    </div>
                </>
            );
        }

        if (originalMessageType === 'photo') {
            return (
                <>
                    <ChatImage uri={options.attachment.objectUrl} />
                    {options.isLastAttachment && (
                        <div style={{ marginTop: '0.5rem' }}>
                            <SanitizedHtmlComponent dirtyHtml={commentBody} />
                        </div>
                    )}
                </>
            );
        }

        if (options.isLastAttachment) {
            return <SanitizedHtmlComponent dirtyHtml={commentBody} />;
        }
    }

    if (options?.previewUrl?.url) {
        return (
            <>
                <URLPreview url={options.previewUrl.url} urlPreviewPromise={options.previewUrl.promise} />
                <div style={{ marginTop: '0.5rem' }}>
                    <SanitizedHtmlComponent dirtyHtml={commentBody} />
                </div>
            </>
        );
    }

    return <SanitizedHtmlComponent dirtyHtml={commentBody} />;
}

function getBoxData(attachment: Attachment) {
    const type: MessageType['type'] = getMessageTypeByAttachment(attachment).originalMessageType;

    if (type === 'audio') {
        return {
            audioURL: attachment.objectUrl
        } as IAudioMessageProps['data'];
    }

    if (type === 'file') {
        return {
            uri: attachment.objectUrl,
            size: getHumanReadableFileSize(attachment.size)
        } as IFileMessageProps['data'];
    }

    if (type === 'photo') {
        return {
            uri: attachment.objectUrl
        } as IPhotoMessageProps['data'];
    }

    if (type === 'video') {
        return {
            videoURL: attachment.objectUrl,
            status: {
                click: true,
                loading: 0.5,
                download: true
            }
        } as IVideoMessageProps['data'];
    }
}

function convertToBox(comment: Comment, options: BoxOptions): MessageType {
    const defaultData = {
        titleColor: options?.isMine ? PRIMARY_COLOR : TICKET_STATUS_COLOR_SOLVED,
        forwarded: false,
        replyButton: false,
        removeButton: false,
        status: 'sent',
        notch: false,
        retracted: false,
        focus: false
    };

    const messageData: MessageType = {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        text: getBoxText(comment, options),

        id: options.attachment ? `${comment.id}-${options.attachment.id}` : `${comment.id}`,
        position: options.isMine ? 'right' : 'left',
        title: comment.creator.fullName,
        date: comment.createdAt,
        type: (options.attachment
            ? getMessageTypeByAttachment(options.attachment).computedMessageType
            : 'text') as MessageType['type'],

        ...defaultData
    };

    if (options.attachment) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        messageData.data = getBoxData(options.attachment);
    }

    return messageData;
}

export default function GSocialChatList({ comments, ...rest }: GSocialChatListProps) {
    const messageListReferance = useRef();
    const chatListRef = useRef<HTMLDivElement>(null);

    useScroll(chatListRef, { behavior: 'instant' }, [comments]);

    const {
        data: {
            userInfo: { userId }
        }
    } = useSession();
    const gSocialStatus = useGSocialStatus();

    const commentsAndAttachmentsAsBox = useMemo(
        () => getCommentsAndAttachmentsAsBox(injectUrlsToComments(comments), userId),
        [comments, userId]
    );

    const handleOnClick = (message: MessageType, i: number, e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();

        if (!e.target) return;
        if (!(e.target instanceof Element)) return;

        const anchorEl = e.target.closest('a');

        if (anchorEl instanceof HTMLAnchorElement) {
            const href = anchorEl.href;
            return isInternalLink(href) ? router.push(href) : window.open(href, '_blank');
        }

        if (message.type === 'photo' && e.target instanceof HTMLImageElement) {
            window.open(message?.data?.uri, '_blank');
            return;
        }

        if (
            message.type === 'file' &&
            (e.target instanceof HTMLDivElement ||
                e.target instanceof SVGElement ||
                e.target instanceof HTMLButtonElement)
        ) {
            let buttonNode: ParentNode | null = e.target;
            while (!(buttonNode instanceof HTMLButtonElement)) {
                buttonNode = buttonNode.parentNode;

                if (!buttonNode) return;
            }
            buttonNode.parentElement instanceof HTMLDivElement &&
                buttonNode.parentElement.className === 'rce-mbox-file' &&
                window.open(message?.data?.uri, '_blank');
            return;
        }
    };

    return (
        <div className="gsocial-chat-list" style={{ ...styles.chatContainer, ...(rest.style || {}) }}>
            <div ref={chatListRef} style={styles.chatListContainer}>
                {commentsAndAttachmentsAsBox.length < 1 ? (
                    <Empty />
                ) : (
                    <MessageList
                        onClick={handleOnClick}
                        referance={messageListReferance}
                        lockable={true}
                        toBottomHeight={'100%'}
                        dataSource={commentsAndAttachmentsAsBox}
                    />
                )}
            </div>
            <div style={styles.chatInputContainer}>
                <ChatInput isDisabled={gSocialStatus === 'inactive'} />
            </div>
        </div>
    );
}
